import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { RefreshCcw, Settings, ChevronLeft } from 'lucide-react';

interface HeaderProps {
  headTitle?: React.ReactNode;
  children?: React.ReactNode;
  goBack?: () => void;
  refresh?: () => void;
  settings?: () => void;
  handleHeadClick?: (type: string, index: number) => void;
  isShowBack?: boolean;
}

export interface HeaderRef {
  setActiveTab: (index: number) => void;
}

const Header = forwardRef<HeaderRef, HeaderProps>(
  ({ headTitle, children, goBack, refresh, settings, handleHeadClick, isShowBack = true }, ref) => {
    const [activeTab, setActiveTab] = useState(0);

    useImperativeHandle(ref, () => ({
      setActiveTab
    }));

    const switchTab = (index: number, type: string) => {
      setActiveTab(index);
      handleHeadClick && handleHeadClick(type, index);
    };

    const handleClick = () => {
      if (isShowBack) {
        // 3. 调用父组件传递的函数，传递数据（类似 emit）
        goBack && goBack();
      }
    };

    const handleRefresh = () => {
      refresh && refresh();
    };

    const handleSettings = () => {
      settings && settings();
    };

    return (
      <div className="flex items-center justify-between px-4 py-6">
        {/* 左侧箭头图标 */}
        <div
          className={`h-4 w-4 cursor-pointer ${isShowBack ? 'block' : 'opacity-0'}`}
          onClick={handleClick}
        >
          <ChevronLeft className="h-full w-full" />
        </div>

        {/* AI智能投顾助手标题和Tab切换 */}
        {children || headTitle || (
          <div className="flex cursor-pointer rounded-md bg-[#DEDAF7] px-1 py-0.5">
            {/* Tab切换容器 */}
            <div className="relative flex">
              {/* 背景滑块 - 修复位置问题 */}
              <div
                className="absolute bottom-0.5 top-0.5 rounded-md bg-white transition-all duration-300 ease-in-out"
                style={{
                  // 根据激活的Tab计算左侧位置
                  left: activeTab === 0 ? '0%' : '66.67%',
                  // 根据激活的Tab计算宽度
                  width: activeTab === 0 ? '66.67%' : '33.33%'
                }}
              />

              {/* AI智能投顾助手 Tab */}
              <div
                className="relative min-w-[120px] cursor-pointer rounded-md px-1.5 py-1 text-center transition-all duration-300 ease-in-out"
                onClick={() => switchTab(0, 'aiAdvisor')}
              >
                <span
                  className={`text-base font-semibold transition-all duration-300 ${
                    activeTab === 0
                      ? 'bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] bg-clip-text text-transparent'
                      : 'text-gray-500'
                  }`}
                >
                  AI智能投顾助手
                </span>
              </div>

              {/* 对话 Tab */}
              <div
                className="relative min-w-[60px] cursor-pointer rounded-md px-1.5 py-1 text-center transition-all duration-300 ease-in-out"
                onClick={() => switchTab(1, 'dialog')}
              >
                <span
                  className={`text-base font-semibold transition-all duration-300 ${
                    activeTab === 1
                      ? 'bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] bg-clip-text text-transparent'
                      : 'text-gray-500'
                  }`}
                >
                  对话
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 右侧菜单和搜索图标 */}
        <div className="flex items-center gap-2">
          {/* 菜单图标 */}
          <div className="h-4 w-4 cursor-pointer" onClick={handleRefresh}>
            <RefreshCcw className="h-full w-full" />
          </div>

          {/* 搜索图标 */}
          <div className="h-4 w-4 cursor-pointer" onClick={handleSettings}>
            <Settings className="h-full w-full" />
          </div>
        </div>
      </div>
    );
  }
);

Header.displayName = 'Header';

export default Header;
